import * as React from 'react'
import { Check, Circle } from 'lucide-react'
import { cn } from '@klear/ui/lib/utils'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@klear/ui/components/tooltip'
import { KlearDate } from '@klear/date'
import isArray from 'lodash/isArray'

export interface ProgressStep {
  title: string
  complete: boolean
  date?: KlearDate
}

export type StageStatus = 'warning' | 'error' | undefined

export interface ProgressStage {
  title: string
  steps: ProgressStep[]
  status?: StageStatus
  message?: string
}

export type ProgressStatusSize = 'small' | 'medium' | 'xl'

export type StageVisualState = 'default' | 'partiallyDone' | 'done' | 'warning' | 'error'

export interface ProgressStatusProps {
  stages: ProgressStage[]
  size?: ProgressStatusSize
  className?: string
}

export function calculateProgressScore(stages: ProgressStage[]): number {
  return stages.reduce((total, stage) => {
    const totalSteps = stage.steps.length
    if (totalSteps === 0) return total

    const completedSteps = stage.steps.filter((s) => s.complete).length
    const stepValue = 1 / totalSteps
    let stageScore = completedSteps * stepValue

    if (stage.status === 'warning') {
      stageScore += 0.1
    } else if (stage.status === 'error') {
      stageScore += 0.2
    }

    return total + stageScore
  }, 0)
}

const getHeightClass = (size: ProgressStatusSize) => {
  switch (size) {
    case 'small':
      return 'h-2'
    case 'medium':
      return 'h-5'
    case 'xl':
      return 'h-8'
  }
}

const getGapClass = (size: ProgressStatusSize) => {
  switch (size) {
    case 'small':
      return 'gap-[3px]'
    case 'medium':
    case 'xl':
      return 'gap-[4px]'
  }
}

const getBorderClass = (size: ProgressStatusSize) => {
  switch (size) {
    case 'small':
      return 'border'
    case 'medium':
    case 'xl':
      return 'border-2'
  }
}

const getStatusColorClass = (status: StageStatus) => {
  switch (status) {
    case 'warning':
      return 'text-orange-300'
    case 'error':
      return 'text-red-300'
    default:
      return 'text-grey-300'
  }
}

function isPartiallyDone(stage: ProgressStage): boolean {
  const completedSteps = stage.steps.filter((s) => s.complete).length
  const totalSteps = stage.steps.length
  return completedSteps > 0 && completedSteps < totalSteps
}

function computeStageState(stage: ProgressStage): StageVisualState {
  if (stage.status === 'error') return 'error'
  if (stage.status === 'warning') return 'warning'

  const completedSteps = stage.steps.filter((s) => s.complete).length
  const totalSteps = stage.steps.length

  if (completedSteps === 0) return 'default'
  if (completedSteps === totalSteps) return 'done'
  return 'partiallyDone'
}

const getStageStyles = (state: StageVisualState) => {
  switch (state) {
    case 'done':
      return 'bg-green-300 border-green-400 dark:bg-green-700 dark:border-green-600'
    case 'partiallyDone':
      return 'border-border'
    case 'error':
      return 'bg-red-200 border-red-300 dark:bg-red-800 dark:border-red-700'
    case 'warning':
      return 'bg-orange-200 border-orange-300 dark:bg-orange-800 dark:border-orange-700'
    case 'default':
    default:
      return 'bg-muted border-border'
  }
}

export const ProgressStatus = React.forwardRef<HTMLDivElement, ProgressStatusProps>(
  ({ stages, size = 'small', className }, ref) => {
    const heightClass = getHeightClass(size)
    const gapClass = getGapClass(size)
    const borderClass = getBorderClass(size)

    if (!stages || !isArray(stages) || stages.length === 0) {
      return null
    }

    return (
      <TooltipProvider delayDuration={0}>
        <div ref={ref} className={cn('w-full', className)}>
          <div className={cn('flex', gapClass)}>
            {stages.map((stage, index) => {
              if (!stage || !isArray(stage.steps)) {
                return null
              }

              const stageState = computeStageState(stage)
              const stageStyles = getStageStyles(stageState)
              const isFirst = index === 0
              const isLast = index === stages.length - 1
              const partiallyDone = isPartiallyDone(stage)
              const shouldShowStripes =
                partiallyDone && (stageState === 'partiallyDone' || stageState === 'warning' || stageState === 'error')

              return (
                <Tooltip key={index}>
                  <TooltipTrigger asChild>
                    <div
                      className={cn(
                        'flex-1 cursor-pointer transition-all',
                        borderClass,
                        heightClass,
                        isFirst && 'rounded-l-full',
                        isLast && 'rounded-r-full',
                        !isFirst && !isLast && 'rounded-none',
                        stageStyles,
                        shouldShowStripes &&
                          (stageState === 'warning' || stageState === 'error'
                            ? 'progress-stripes-warning'
                            : 'progress-stripes')
                      )}
                    />
                  </TooltipTrigger>
                  <TooltipContent
                    side="top"
                    className="bg-gray-800 text-white p-3 min-w-[200px]"
                    onPointerDownOutside={(e) => e.preventDefault()}
                  >
                    <div className="space-y-1">
                      <div className="font-semibold text-sm">{stage.title}</div>
                      <div className="text-xs text-gray-300 space-y-1 pt-1">
                        {stage.steps.map((step, stepIndex) => (
                          <div key={stepIndex} className="flex items-center justify-between gap-2 w-full">
                            <div className="flex items-center gap-1.5 flex-shrink-0">
                              {step.complete ? (
                                <Check className="h-3 w-3 text-green-400" />
                              ) : (
                                <Circle className="h-3 w-3 text-gray-400" />
                              )}
                              <span>{step.title}</span>
                            </div>
                            {step.date && <span className="text-gray-400 ml-auto">{KlearDate.format(step.date)}</span>}
                          </div>
                        ))}
                      </div>
                      {stage.message && (
                        <div className="text-xs pt-1 border-t border-gray-700">
                          <div className={getStatusColorClass(stage.status)}>{stage.message}</div>
                        </div>
                      )}
                    </div>
                  </TooltipContent>
                </Tooltip>
              )
            })}
          </div>
        </div>
      </TooltipProvider>
    )
  }
)
ProgressStatus.displayName = 'ProgressStatus'
